// Portfolio Service - Firebase Firestore + Storage for Portfolio Items
import {
    collection,
    doc,
    getDocs,
    getDoc,
    setDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject
} from 'firebase/storage';
import { db, storage } from './firebase';
import { Project } from '../types';

const PORTFOLIO_COLLECTION = 'portfolio';

// Generate a unique ID
const generateId = (): string => {
    return `portfolio_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// Helper: Wait for a promise with timeout
const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> => {
    const timeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), ms);
    });
    return Promise.race([promise, timeout]);
};

// ==================== PORTFOLIO CRUD ====================

// Get all portfolio items - Firebase ONLY
export const getPortfolio = async (): Promise<Project[]> => {
    try {
        console.log('📡 Fetching portfolio from Firebase...');
        const portfolioRef = collection(db, PORTFOLIO_COLLECTION);
        const snapshot = await withTimeout(getDocs(portfolioRef), 15000);

        console.log('📦 Found', snapshot.docs.length, 'portfolio items in Firebase');

        const portfolio = snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data()
        })) as Project[];

        // Sort by createdAt client-side
        portfolio.sort((a: any, b: any) => {
            const dateA = a.createdAt?.toDate?.() || new Date(0);
            const dateB = b.createdAt?.toDate?.() || new Date(0);
            return dateB.getTime() - dateA.getTime();
        });

        return portfolio;
    } catch (error) {
        console.error('❌ Firebase error fetching portfolio:', error);
        return [];
    }
};

// Get single portfolio item by ID
export const getPortfolioById = async (id: string): Promise<Project | null> => {
    try {
        const docRef = doc(db, PORTFOLIO_COLLECTION, id);
        const docSnap = await withTimeout(getDoc(docRef), 10000);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as Project;
        }

        return null;
    } catch (error) {
        console.error('❌ Error fetching portfolio item:', error);
        return null;
    }
};

// Add new portfolio item - using setDoc with generated ID to avoid abort issues
export const addPortfolio = async (portfolioData: Partial<Project>): Promise<string | null> => {
    const newId = generateId();
    console.log('📝 Adding portfolio item with ID:', newId, 'Title:', portfolioData.title);

    try {
        const docRef = doc(db, PORTFOLIO_COLLECTION, newId);
        const newPortfolio = {
            ...portfolioData,
            id: newId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        console.log('📤 Sending to Firebase using setDoc...');

        // Use setDoc instead of addDoc to have more control
        await withTimeout(setDoc(docRef, newPortfolio), 15000);

        console.log('✅ Portfolio item added to Firebase:', newId);
        return newId;
    } catch (error: any) {
        // Check if it's an abort error - still might have succeeded
        if (error?.name === 'AbortError' || error?.message?.includes('abort')) {
            console.warn('⚠️ AbortError occurred, but checking if document was created...');
            // Wait a bit and check if document exists
            await new Promise(resolve => setTimeout(resolve, 1000));
            try {
                const checkDoc = await getDoc(doc(db, PORTFOLIO_COLLECTION, newId));
                if (checkDoc.exists()) {
                    console.log('✅ Document was created despite abort error:', newId);
                    return newId;
                }
            } catch (checkError) {
                console.error('❌ Error checking document:', checkError);
            }
        }

        console.error('❌ Error adding portfolio to Firebase:', error);
        console.error('❌ Error name:', error?.name);
        console.error('❌ Error code:', error?.code);
        console.error('❌ Error message:', error?.message);
        return null;
    }
};

// Update existing portfolio item
export const updatePortfolio = async (id: string, updates: Partial<Project>): Promise<boolean> => {
    if (!id) {
        console.error('❌ Invalid ID for update');
        return false;
    }

    try {
        const docRef = doc(db, PORTFOLIO_COLLECTION, id);
        await withTimeout(updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp(),
        }), 15000);
        console.log('✅ Portfolio item updated in Firebase:', id);
        return true;
    } catch (error: any) {
        // Check if it's an abort error
        if (error?.name === 'AbortError' || error?.message?.includes('abort')) {
            console.warn('⚠️ AbortError on update, checking if update succeeded...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            try {
                const checkDoc = await getDoc(doc(db, PORTFOLIO_COLLECTION, id));
                if (checkDoc.exists()) {
                    console.log('✅ Update likely succeeded despite abort');
                    return true;
                }
            } catch (checkError) {
                console.error('❌ Error checking update:', checkError);
            }
        }
        console.error('❌ Error updating portfolio item:', error);
        return false;
    }
};

// Delete portfolio item
export const deletePortfolio = async (id: string): Promise<boolean> => {
    if (!id || typeof id !== 'string' || id.trim() === '') {
        console.error('❌ Invalid portfolio ID provided for deletion:', id);
        return false;
    }

    console.log('🗑️ Attempting to delete portfolio item:', id);

    try {
        // Try to delete associated image from storage
        try {
            const imageRef = ref(storage, `portfolio/${id}/image`);
            await deleteObject(imageRef);
            console.log('🖼️ Deleted associated image from storage');
        } catch (e) {
            console.log('ℹ️ No associated image found in storage (this is OK)');
        }

        const docRef = doc(db, PORTFOLIO_COLLECTION, id);
        await withTimeout(deleteDoc(docRef), 15000);
        console.log('✅ Portfolio item deleted from Firebase:', id);
        return true;
    } catch (error: any) {
        // Check if it's an abort error
        if (error?.name === 'AbortError' || error?.message?.includes('abort')) {
            console.warn('⚠️ AbortError on delete, checking if delete succeeded...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            try {
                const checkDoc = await getDoc(doc(db, PORTFOLIO_COLLECTION, id));
                if (!checkDoc.exists()) {
                    console.log('✅ Delete succeeded despite abort');
                    return true;
                }
            } catch (checkError) {
                console.error('❌ Error checking delete:', checkError);
            }
        }
        console.error('❌ Error deleting portfolio item:', error);
        return false;
    }
};

// ==================== IMAGE UPLOAD ====================

export const uploadPortfolioImage = async (
    file: File,
    portfolioId?: string
): Promise<string | null> => {
    try {
        const timestamp = Date.now();
        const extension = file.name.split('.').pop();
        const fileName = portfolioId
            ? `portfolio/${portfolioId}/image-${timestamp}.${extension}`
            : `portfolio/temp/image-${timestamp}.${extension}`;

        const storageRef = ref(storage, fileName);

        const snapshot = await uploadBytes(storageRef, file);
        console.log('✅ Portfolio image uploaded to Firebase Storage');

        const downloadURL = await getDownloadURL(snapshot.ref);
        return downloadURL;
    } catch (error) {
        console.error('❌ Error uploading portfolio image:', error);
        return null;
    }
};
